import clsx from 'clsx'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import React from 'react'

import { StickyCellBorder } from './StickyCellBorder'

interface TableColumnHeaderProps extends Omit<React.ThHTMLAttributes<HTMLTableCellElement>, 'className'> {
  align?: 'left' | 'right' | 'center';
  children: React.ReactNode;
  onSort?: React.MouseEventHandler<HTMLButtonElement>
  sorted?: false | 'asc' | 'desc';
  sticky?: boolean;
}

export function TableColumnHeader ({
  align = 'left',
  children,
  onSort,
  sorted,
  sticky,
  ...props
}: TableColumnHeaderProps) {
  function getAriaSort () {
    if (!onSort || !sorted) return undefined

    if (sorted === 'asc') return 'ascending'
    if (sorted === 'desc') return 'descending'
  }

  function renderSortIndicator () {
    if (!sorted) return null

    if (sorted === 'asc') {
      return <ChevronUpIcon size={16} />
    }

    if (sorted === 'desc') {
      return <ChevronDownIcon size={16} />
    }
  }

  function renderName () {
    if (onSort) {
      return (
        <button
          type='button'
          className={clsx(
            'group/sort-button flex items-center text-nowrap cursor-pointer outline-2 outline-offset-2 outline-transparent focus-visible:outline-thatch-500 transition',
            {
              'text-gray-800 hover:text-gray-800': sorted,
              'text-gray-500 hover:text-gray-800': !sorted,
            }
          )}
          onClick={onSort}
        >
          {children}
          <span
            className={clsx('ml-1 w-4', {
              'text-thatch-400 group-hover/sort-button:text-thatch-500': sorted,
              'text-gray-400 group-hover/sort-button:text-gray-500': !sorted,
            })}
            aria-hidden
          >
            {renderSortIndicator()}
          </span>
        </button>
      )
    }

    return children
  }

  return (
    <th
      {...props}
      aria-sort={getAriaSort()}
      className={clsx(
        'body-sm py-2 pr-[var(--cell-spacing)] first:pl-[var(--cell-spacing)] text-gray-500 bg-white border-b border-gray-200 transition',
        {
          sticky,
          'z-10': sticky,
          'text-left': align === 'left',
          'text-right': align === 'right',
          'text-center': align === 'center',
        }
      )}
      data-sticky={sticky}
      scope='col'
    >
      <div>{renderName()}</div>
      <StickyCellBorder sticky={sticky} />
    </th>
  )
}

TableColumnHeader.displayName = 'Table.ColumnHeader'
